import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { terminalToast } from "@/lib/toast";
import { motion, AnimatePresence } from "framer-motion";
import { VimNavContext, type VimMode } from "@/context/VimNavContextProvider";

interface VimNavProviderProps {
  children: React.ReactNode;
  konamiMode?: boolean;
  onToggleKonami?: (active: boolean) => void;
}

export const VimNavProvider = ({
  children,
  konamiMode = false,
  onToggleKonami,
}: VimNavProviderProps) => {
  const [mode, setMode] = useState<VimMode>("insert");
  const [command, setCommand] = useState("");
  const [lastEscTime, setLastEscTime] = useState<number | null>(null);
  const [isKonami, setIsKonami] = useState<boolean>(konamiMode);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [isEnabled, setIsEnabled] = useState<boolean>(false);
  const [commandHistory, setCommandHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState<number>(-1);

  const navigate = useNavigate();

  const knownCommands = useMemo(
    () => [
      // Navigation commands
      "go home",
      "go about",
      "go about-me", 
      "go projects",
      "go stack",
      "go logs",
      "go card",
      "go digital-card",

      // Theme commands
      "theme tron",
      "theme matrix",
      "theme default",
      "theme random",
      "theme konami",
      "theme normal",

      // Scroll commands
      "scroll up",
      "scroll down",
      "scroll left",
      "scroll right",
      "scroll top",
      "scroll bottom",

      // System commands
      "q!",
      "wq",
      "clear storage",
      "reload",
      "help",
      "version",

      // Fun commands
      "echo hello world",
      "cowsay hello",
      "fortune",
      "date",
      "whoami",

      // VI mode commands
      "set number",
      "set nonumber",
      "vim on",
      "vim off",
    ],
    []
  );

  const toggleVimMode = useCallback(() => {
    setIsEnabled(prev => {
      const newState = !prev;
      if (newState) {
        setMode("normal");
        terminalToast.success("VI mode enabled - Press 'i' for insert, ':' for command");
      } else {
        setMode("insert");
        terminalToast.info("VI mode disabled");
      }
      return newState;
    });
  }, []);

  const runCommand = useCallback(
    (cmd: string) => {
      const trimmed = cmd.trim();
      const [base, ...args] = trimmed.split(" ");

      // Add to command history
      if (trimmed && !commandHistory.includes(trimmed)) {
        setCommandHistory(prev => [trimmed, ...prev.slice(0, 19)]); // Keep last 20 commands
      }

      // VI mode commands
      if (trimmed === "vim on") {
        setIsEnabled(true);
        setMode("normal");
        terminalToast.vim.modeChange("normal");
        return;
      }

      if (trimmed === "vim off") {
        setIsEnabled(false);
        setMode("insert");
        terminalToast.info("VI mode disabled");
        return;
      }

      // System commands
      if (trimmed === "q!" || trimmed === "wq") {
        window.open("", "_self")?.close();
        terminalToast.system.closeAttempt();
        return;
      }

      if (trimmed === "clear storage") {
        localStorage.clear();
        sessionStorage.clear();
        terminalToast.system.clearStorage();
        return;
      }

      if (trimmed === "reload") {
        terminalToast.system.reload();
        window.location.reload();
        return;
      }

      if (trimmed === "version") {
        terminalToast.fun.version();
        return;
      }

      if (trimmed === "date") {
        terminalToast.fun.date();
        return;
      }

      if (trimmed === "whoami") {
        terminalToast.fun.whoami();
        return;
      }

      if (trimmed === "help") {
        terminalToast(
          "VI Commands: h/j/k/l (move), i (insert), : (command), v (visual), Esc Esc (enable/normal)"
        );
        return;
      }

      // Fun commands
      if (base === "cowsay") {
        const message = args.join(" ") || "Hello from the terminal!";
        terminalToast.fun.cowsay(message);
        return;
      }

      if (trimmed === "fortune") {
        const fortunes = [
          "The best way to predict the future is to invent it.",
          "Code is poetry written in logic.",
          "Debugging is like being a detective in a crime movie where you are also the murderer.",
          "There are only 10 types of people: those who understand binary and those who don't.",
          "A good programmer is someone who always looks both ways before crossing a one-way street.",
        ];
        terminalToast.fun.fortune(fortunes[Math.floor(Math.random() * fortunes.length)]);
        return;
      }

      if (base === "echo") {
        const message = args.join(" ");
        if (message) {
          terminalToast(message);
        } else {
          terminalToast.error("Usage: :echo <message>");
        }
        return;
      }

      // Theme commands
      if (trimmed === "theme random") {
        const themes = ["tron", "matrix"];
        const random = themes[Math.floor(Math.random() * themes.length)];
        runCommand(`theme ${random}`);
        return;
      }

      if (base === "theme") {
        const theme = args[0];
        if (theme === "tron" || theme === "konami") {
          setIsKonami(true);
          onToggleKonami?.(true);
          terminalToast.theme.switched("TRON");
        } else if (theme === "matrix" || theme === "default" || theme === "normal") {
          setIsKonami(false);
          onToggleKonami?.(false);
          terminalToast.theme.switched("Matrix");
        } else {
          terminalToast.error("Available themes: tron, matrix, default, konami, normal");
        }
        return;
      }

      // Scroll commands
      if (base === "scroll") {
        const direction = args[0];
        const amount = parseInt(args[1] || "100");

        if (direction === "top") {
          window.scrollTo({ top: 0, behavior: "smooth" });
        } else if (direction === "bottom") {
          window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
        } else if (direction === "down") {
          window.scrollBy({ top: amount, behavior: "smooth" });
        } else if (direction === "up") {
          window.scrollBy({ top: -amount, behavior: "smooth" });
        } else if (direction === "left") {
          window.scrollBy({ left: -amount, behavior: "smooth" });
        } else if (direction === "right") {
          window.scrollBy({ left: amount, behavior: "smooth" });
        } else {
          terminalToast.error("Usage: :scroll <up|down|left|right|top|bottom> [amount]");
        }
        return;
      }

      // Navigation commands
      if (base === "go") {
        const route = args[0];
        if (route) {
          const routeMap: Record<string, string> = {
            home: "/",
            about: "/about-me",
            "about-me": "/about-me",
            projects: "/projects",
            stack: "/stack",
            logs: "/logs",
            card: "/digital-card",
            "digital-card": "/digital-card",
          };

          const path = routeMap[route];
          if (path) {
            navigate(path);
            terminalToast.nav.navigated(route);
          } else {
            terminalToast.error("Available routes: home, about, about-me, projects, stack, logs, card, digital-card");
          }
        } else {
          terminalToast.error("Usage: :go <home|about|about-me|projects|stack|logs|card|digital-card>");
        }
        return;
      }

      terminalToast.vim.invalidCommand(cmd);
    },
    [commandHistory, navigate, onToggleKonami]
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Global VI mode toggle (Ctrl+;)
      if (e.ctrlKey && e.key === ";") {
        e.preventDefault();
        toggleVimMode();
        return;
      }

      // ESC ESC to enable Vim mode or enter normal mode (authentic Vim behavior)
      if (e.key === "Escape") {
        const now = Date.now();
        if (lastEscTime && now - lastEscTime < 300) {
          // Double ESC detected
          if (!isEnabled) {
            // Enable Vim mode and enter normal mode
            setIsEnabled(true);
            setMode("normal");
            terminalToast.success("VI mode enabled - Press 'i' for insert, ':' for command");
          } else {
            // Already enabled, just enter normal mode
            setMode("normal");
            setCommand("");
            setSuggestions([]);
            setHistoryIndex(-1);
            terminalToast.vim.modeChange("normal");
          }
        }
        setLastEscTime(now);
        return;
      }

      // Only handle VI keys if enabled
      if (!isEnabled) return;

      if (mode === "normal") {
        if (e.key === ":") {
          setMode("command");
          setCommand("");
          setSuggestions([]);
          setHistoryIndex(-1);
          e.preventDefault();
          return;
        } else if (e.key === "i") {
          setMode("insert");
          terminalToast.vim.modeChange("insert");
        } else if (e.key === "v") {
          setMode("visual");
          terminalToast.vim.modeChange("visual");
        } else if (["h", "j", "k", "l"].includes(e.key)) {
          const scrollAmount = e.shiftKey ? 200 : 50;
          if (e.key === "h") window.scrollBy({ left: -scrollAmount, behavior: "smooth" });
          if (e.key === "l") window.scrollBy({ left: scrollAmount, behavior: "smooth" });
          if (e.key === "j") window.scrollBy({ top: scrollAmount, behavior: "smooth" });
          if (e.key === "k") window.scrollBy({ top: -scrollAmount, behavior: "smooth" });
        } else if (e.key === "g") {
          // gg to go to top
          setTimeout(() => {
            const handleSecondG = (e2: KeyboardEvent) => {
              if (e2.key === "g") {
                window.scrollTo({ top: 0, behavior: "smooth" });
              }
              document.removeEventListener("keydown", handleSecondG);
            };
            document.addEventListener("keydown", handleSecondG);
          }, 0);
        } else if (e.key === "G") {
          // G to go to bottom
          window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
        }
        e.preventDefault();
      } else if (mode === "command") {
        if (e.key === "Enter") {
          runCommand(command);
          setCommand("");
          setSuggestions([]);
          setHistoryIndex(-1);
          setMode("normal");
        } else if (e.key === "Escape") {
          setCommand("");
          setSuggestions([]);
          setHistoryIndex(-1);
          setMode("normal");
        } else if (e.key === "ArrowUp") {
          if (commandHistory.length > 0) {
            const newIndex = Math.min(historyIndex + 1, commandHistory.length - 1);
            setHistoryIndex(newIndex);
            setCommand(commandHistory[newIndex]);
          }
        } else if (e.key === "ArrowDown") {
          if (historyIndex > 0) {
            const newIndex = historyIndex - 1;
            setHistoryIndex(newIndex);
            setCommand(commandHistory[newIndex]);
          } else if (historyIndex === 0) {
            setHistoryIndex(-1);
            setCommand("");
          }
        } else if (e.key === "Backspace") {
          setCommand(prev => {
            const next = prev.slice(0, -1);
            setSuggestions(knownCommands.filter(cmd => cmd.startsWith(next)));
            return next;
          });
        } else if (e.key === "Tab") {
          if (suggestions.length > 0) {
            setCommand(suggestions[0]);
            setSuggestions([]);
          }
          e.preventDefault();
        } else if (e.key.length === 1) {
          const updated = command + e.key;
          setCommand(updated);
          setSuggestions(knownCommands.filter(cmd => cmd.startsWith(updated)));
        }
        e.preventDefault();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [
    mode,
    command,
    lastEscTime,
    suggestions,
    knownCommands,
    runCommand,
    isEnabled,
    commandHistory,
    historyIndex,
    toggleVimMode,
  ]);

  // Update konami state when prop changes
  useEffect(() => {
    setIsKonami(konamiMode);
  }, [konamiMode]);

  // Theme-aware styling
  const getThemeClasses = () => {
    if (isKonami) {
      return {
        commandBar:
          "bg-[#0A0A0A]/95 text-[#00FFFF] border border-[#00FFFF]/30 shadow-[0_0_15px_#00FFFF33]",
        suggestions: "bg-[#0A0A0A]/95 text-[#00FFFF] border border-[#00FFFF]/20",
        modeIndicator: "text-[#00FFFF] bg-[#0A0A0A]/80 border border-[#00FFFF]/30",
        cursor: "bg-[#00FFFF]",
      };
    } else {
      return {
        commandBar: "bg-black/95 text-green-400 border border-green-400/30 shadow-lg",
        suggestions: "bg-black/95 text-green-300 border border-green-400/20",
        modeIndicator: "text-green-400 bg-black/80 border border-green-400/30",
        cursor: "bg-green-400",
      };
    }
  };

  const themeClasses = getThemeClasses();

  return (
    <VimNavContext.Provider
      value={{
        mode,
        command,
        setMode,
        konamiMode: isKonami,
        isEnabled,
        toggleVimMode,
      }}
    >
      {children}

      <AnimatePresence>
        {isEnabled && mode === "command" && (
          <>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              className={`fixed bottom-4 left-4 px-4 py-2 ${themeClasses.commandBar} z-[10000] rounded-lg font-mono text-sm backdrop-blur-md`}
            >
              <span className="opacity-70">:</span>
              {command}
              <span className={`ml-1 inline-block h-4 w-2 ${themeClasses.cursor} animate-pulse`} />
            </motion.div>

            {suggestions.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                className={`fixed bottom-16 left-4 z-[10000] max-w-sm rounded-lg ${themeClasses.suggestions} px-3 py-2 font-mono text-xs backdrop-blur-md`}
              >
                {suggestions.slice(0, 5).map((s, i) => (
                  <div key={i} className={`py-1 ${i === 0 ? "opacity-100" : "opacity-70"}`}>
                    {s}
                  </div>
                ))}
              </motion.div>
            )}
          </>
        )}

        {isEnabled && mode !== "insert" && mode !== "command" && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className={`fixed bottom-4 left-4 ${themeClasses.modeIndicator} z-[10000] rounded-lg px-3 py-1 font-mono text-xs backdrop-blur-md`}
          >
            -- {mode.toUpperCase()} --
          </motion.div>
        )}
      </AnimatePresence>

      {/* VI Mode Toggle Hint */}
      {!isEnabled && (
        <div className="fixed right-4 bottom-4 z-40 opacity-30 transition-opacity hover:opacity-70">
          <div
            className={`${themeClasses.modeIndicator} rounded px-2 py-1 font-mono text-xs backdrop-blur-md`}
          >
            ESC ESC for VI mode
          </div>
        </div>
      )}
    </VimNavContext.Provider>
  );
};
